import React from 'react';
import dynamic from 'next/dynamic';

import config from 'config';
import writeDebugLog from 'utils/writeDebugLog';

const FreestarAdSlot = dynamic(
  () => import('@freestar/pubfig-adslot-react-component').then((m) => m.default),
  { ssr: false },
);

export interface AdConfig {
  adClass?: string;
  adId: string;
  adSize: string;
  adSizeDesktop?: googletag.GeneralSize;
  adSizeMappings?: googletag.SizeMappingArray;
  adSizeMappingsSHE?: Array<Array<number> | number | string>;
  adSizeMobile?: googletag.GeneralSize;
  adSizes: googletag.GeneralSize;
  adTargets: Array<Array<string>>;
  adUnit: string;
  adViewType: string;
  additionalMappingSizes?: googletag.SizeMappingArray;
  autoRefresh?: boolean;
  collapseEmpty?: boolean;
  customAdSize?: Array<number>;
  fluid?: boolean;
  freestarPlacementName?: string;
  isOutOfPage?: boolean;
}

interface FreestarAdProps {
  adConfig: AdConfig;
  useUuid?: boolean;
}

const FREESTAR_KEY_MAP: Record<string, string> = {
  pub_meta_1: 'Cam_ID',
  pub_meta_2: 'Air_temperature',
  pub_meta_3: 'Wind',
  pub_meta_4: 'Spot_ID',
  pub_meta_5: 'Water_Temp',
  pub_meta_6: 'User_Type',
  pub_meta_8: 'Surf_Height',
  pub_meta_9: 'Surf_Conditions_Rating',
  pub_meta_10: 'Site_Section',
};

const targetsToTargeting = (
  adTargets?: Array<Array<string>>,
): Record<string, string> | undefined => {
  if (!adTargets?.length) return undefined;
  return adTargets.reduce<Record<string, string>>((acc, [key, value]) => {
    if (key && value !== undefined) {
      const canonicalKey = FREESTAR_KEY_MAP[key] ?? key;
      acc[canonicalKey] = String(value);
    }
    return acc;
  }, {});
};

const FreestarAd: React.FC<FreestarAdProps> = ({ adConfig }) => {
  const { adId, adClass, adTargets, freestarPlacementName } = adConfig;
  const publisher = config.freestar?.publisher;

  if (!publisher || !freestarPlacementName) {
    writeDebugLog(
      `FreestarAd skipped: publisher=${publisher} freestarPlacementName=${freestarPlacementName} adId=${adId}`,
    );
    return (
      <div className="quiver-google-dfp" data-testid="freestar-ad-empty" suppressHydrationWarning />
    );
  }

  return (
    <div className="quiver-google-dfp" suppressHydrationWarning>
      <FreestarAdSlot
        publisher={publisher}
        placementName={freestarPlacementName}
        slotId={adId}
        targeting={targetsToTargeting(adTargets)}
        classList={adClass ? [adClass] : undefined}
      />
    </div>
  );
};

export default FreestarAd;
